/**
 * Keeps one line of "Shipping Insurance" in the cart whenever the cart holds
 * anything else, priced by Australia Post's own Extra Cover formula:
 *
 *   · cart up to AUD $300  → "Up to $100 · Free" — the cover Australia Post
 *     includes in every parcel. Silvia's rule (25-Aug): no paid insurance on
 *     small orders, so they never see postage + insurance stacked up. The line
 *     stays visible at $0 so the rule explains itself in the cart.
 *   · cart over AUD $300   → tier = ceil((subtotal − $100) / $100), one $2.50
 *     block per started $100 after the first — the full cover for the order,
 *     which is what Australia Post charges her whatever the cart shows.
 *
 * One variant per tier ("Up to $100" … "Up to $5,000"), quantity always 1 —
 * the shipping-insurance apps' pattern (Navidium, Route). The line reads as
 * coverage, never as "× 9 units". The variant list arrives in order from
 * snippets/cart-insurance.liquid: index N = N paid blocks, index 0 = free.
 *
 * Why this exists: a shipping rate in Shopify can be conditioned on weight OR
 * price, never both. Postage depends on weight, insurance on value — so the
 * postage bands stay pure weight, and this line carries the value half.
 *
 * ⚠️ Two bugs the first version shipped with, both learned the hard way:
 *
 * · Horizon dispatches `shopify:cart:lines-update` BEFORE its cart request
 *   finishes — the event carries a deferred `promise` that settles when the
 *   mutation lands. Waiting a fixed 350ms and reading /cart.js raced that
 *   request: the item wasn't in the cart yet, the sync computed "nothing to
 *   insure" and never ran again. The sync now waits on `event.promise`.
 *
 * · Mutating the cart is not enough — the drawer is already painted. The UI
 *   only re-renders on a real `CartLinesUpdateEvent` whose `promise` resolves;
 *   the first version emitted a made-up event nobody listens to. The class is
 *   imported from Shopify's own module (the import map resolves it) and the
 *   promise resolves with an empty detail, which sends Horizon's cart
 *   component down its "refetch my section" path.
 *
 * Other notes, each one paid for:
 * · The subtotal EXCLUDES the insurance line itself, or the fee would feed its
 *   own price and a $199 cart would oscillate between 1 and 2 units.
 * · If the shopper deletes the line, the next sync puts it back. It is not an
 *   optional extra; it is how postage insurance is charged.
 * · Changes go through /cart/change.js with the line KEY, not the variant id:
 *   the same variant could appear twice if a shopper re-adds it manually.
 * · The variant id arrives via a JSON <script> block (legal inside <head>,
 *   where a <div> is not), so this file has no hardcoded store data.
 */

const SETTINGS_ID = 'cart-insurance-settings';
const FREE_CENTS = 100 * 100;        // Australia Post covers the first $100 of every parcel
const PAID_FROM_CENTS = 300 * 100;   // Silvia's rule: paid cover only on carts over $300
const BLOCK_CENTS = 100 * 100;

let syncing = false;
let scheduled = false;

function settings() {
  const el = document.getElementById(SETTINGS_ID);
  if (!el) return null;
  try {
    const parsed = JSON.parse(el.textContent);
    const variants = Array.isArray(parsed.variants) ? parsed.variants.map(Number) : [];
    return variants.length > 0 ? { variants } : null;
  } catch {
    return null;
  }
}

async function readCart() {
  const res = await fetch('/cart.js', { headers: { Accept: 'application/json' } });
  if (!res.ok) throw new Error(`cart.js ${res.status}`);
  return res.json();
}

function targetTier(cart, variants) {
  let subtotal = 0;
  let others = 0;
  for (const item of cart.items) {
    if (variants.includes(item.variant_id)) continue;
    subtotal += item.final_line_price;
    others += item.quantity;
  }
  // Nothing to insure: an empty cart, or one holding only the insurance line.
  if (others === 0) return null;
  // ⚠️ /cart.js entrega los precios en la moneda del COMPRADOR — la tienda
  // presenta AUD, GBP y USD. La fórmula de Extra Cover está definida en AUD,
  // así que el subtotal se convierte de vuelta con la tasa que Shopify publica
  // en cada página. Sin esto, un comprador de EEUU leía el bebé de $950 AUD
  // como ~$620 USD y compraba tres tramos menos de cobertura. El precio del
  // tramo elegido sí se muestra convertido, y eso es correcto: paga en su
  // moneda la cobertura en AUD.
  const rate = Number(window.Shopify && window.Shopify.currency && window.Shopify.currency.rate) || 1;
  const audSubtotal = subtotal / rate;
  if (audSubtotal <= PAID_FROM_CENTS) return 0;
  // La escalera termina en el tope de $5,000 de la póliza de Australia Post.
  return Math.min(Math.ceil((audSubtotal - FREE_CENTS) / BLOCK_CENTS), variants.length - 1);
}

async function notifyThemeUI() {
  try {
    const { CartLinesUpdateEvent } = await import('@shopify/events');
    document.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'update',
        context: 'cart-insurance',
        lines: [],
        // No `sections` in the detail on purpose: that resolves into the cart
        // component's fallback branch, which refetches its own section fresh.
        promise: Promise.resolve({ detail: {} }),
      })
    );
  } catch (error) {
    console.warn('[cart-insurance] UI refresh failed', error);
  }
}

async function sync() {
  const config = settings();
  if (!config || syncing) return;
  syncing = true;
  try {
    const cart = await readCart();
    const lines = cart.items.filter((item) => config.variants.includes(item.variant_id));
    const tier = targetTier(cart, config.variants);
    const wanted = tier === null ? null : config.variants[tier];

    // Una variante por tramo, siempre cantidad 1 — el patrón de las apps de
    // seguro. Cambiar de tramo es quitar la variante vieja y poner la nueva:
    // /cart/change.js no sabe intercambiar variantes.
    const correct = lines.length === 1 && wanted !== null
      && lines[0].variant_id === wanted && lines[0].quantity === 1;
    if (correct || (lines.length === 0 && wanted === null)) return;

    for (const line of lines) {
      await fetch('/cart/change.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ id: line.key, quantity: 0 }),
      });
    }
    if (wanted !== null) {
      await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items: [{ id: wanted, quantity: 1 }] }),
      });
    }
    await notifyThemeUI();
  } catch (error) {
    console.warn('[cart-insurance]', error);
  } finally {
    syncing = false;
  }
}

function schedule() {
  if (syncing || scheduled) return;
  scheduled = true;
  setTimeout(() => {
    scheduled = false;
    sync();
  }, 150);
}

document.addEventListener('shopify:cart:lines-update', (event) => {
  // ⚠️ Horizon fires this before its own request settles; the promise is the
  // only honest "the cart has actually changed" signal.
  const promise = /** @type {{promise?: Promise<unknown>}} */ (event).promise;
  if (promise && typeof promise.finally === 'function') {
    promise.finally(schedule);
  } else {
    schedule();
  }
});

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', schedule);
} else {
  schedule();
}
